import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAreFeatureFlagsReady } from '../../modules/App/Providers/FeatureFlag'
import { useFeatureFlags } from '../useFeatureFlags'
import { AppointmentType } from '../../constants'
import { TrialGate, useTrialGate } from '../useTrialGate'
import { getTrialGate, TRIAL_GATES, TRIAL_GATE_FLAG_NAMES, TRIAL_GATE_TYPES } from '../useTrialGate/trialGates'

type TrialGateReasons = Partial<Record<AppointmentType, string>>

// Widened to AppointmentType on the way out. Consumers hold plain appointment types - they ask
// "is this one blocked?" and "what does it say?" - so keeping the narrow union here would make
// every membership test and every reason lookup cast at the call site.
type BlockedAppointmentTypeResult = Omit<TrialGate, 'blockedAppointmentTypes'> & {
  blockedAppointmentTypes: AppointmentType[]
  /**
   * Ready-to-render copy per blocked type - built once here instead of by every consumer, so
   * callers don't need to know the i18n key or interpolation shape. Keyed by type rather than a
   * single string because one trial decision can block several types, and they don't all read the
   * same to the user.
   */
  reasons: TrialGateReasons
  /**
   * The trial-gate directions whose flag is currently on, whether or not either of them ended up
   * blocking anything. Analytics needs this for the unknown-trial-data case: nothing is blocked
   * then, so blockedAppointmentTypes alone cannot say which feature hit the fallback.
   */
  activeTrialGateTypes: AppointmentType[]
  /**
   * Directions that might still turn out to be blocked once everything this gate waits on has
   * resolved. A surface that lets one of these be picked before then hands the user a booking the
   * rule would have refused, and nothing revisits the decision afterwards: the type-selection route
   * builds its own form defaults in New/index.tsx rather than going through the substitution.
   *
   * Two waits, needing different answers:
   * - flags unresolved: every flag reads falsy, so an off direction is indistinguishable from an
   *   unknown one. Every direction is pending.
   * - flags resolved, opportunity or server time in flight: only the switched-on directions are, so
   *   each direction still rolls out independently of the other.
   *
   * Empty out of scope, so nothing outside US Teleaudiology is ever held back.
   */
  pendingTrialGateTypes: AppointmentType[]
}

const NO_TYPES: AppointmentType[] = []
const NO_REASONS: TrialGateReasons = {}

const INERT_RESULT: BlockedAppointmentTypeResult = {
  isInScope: false,
  isLoading: false,
  trialEndDate: null,
  isTrialDataUnknown: false,
  blockedAppointmentTypes: NO_TYPES,
  reasons: NO_REASONS,
  activeTrialGateTypes: NO_TYPES,
  pendingTrialGateTypes: NO_TYPES,
}

// "Aug 10, 2026" rather than a slash-separated format: i18next escapes interpolated values by
// default (`/` becomes `&#x2F;`), which rendered literally in plain text instead of a real slash.
// timeZone: 'UTC' is required - trialEndDate is a date-only value serialized at UTC midnight, so
// formatting it in the viewer's local timezone (the default) renders the previous calendar day
// for anyone west of UTC, e.g. "Aug 31" for a trialEndDate of "2026-09-01T00:00:00.000Z".
//
// This deliberately renders trialEndDate even though trialExpiration is what decides in/out of
// trial (PX-3720). trialEndDate *is* the customer-facing last day of the trial, whereas
// trialExpiration is the midnight *after* it - formatting that instead would tell the customer
// their trial ended a day later than it did. Do not "fix" this to use trialExpiration.
const formatLastTrialDay = (trialEndDateIso: string | undefined) =>
  trialEndDateIso &&
  new Date(trialEndDateIso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

/**
 * The flag-gated result of useTrialGate: whichever appointment types the trial-status rule would
 * block, minus any whose feature flag is off. A type whose flag is off is always inert here, so
 * each direction can be rolled out and back independently of the other.
 */
const useBlockedAppointmentType = (): BlockedAppointmentTypeResult => {
  const { t } = useTranslation()
  const trialGate = useTrialGate()
  const areFeatureFlagsReady = useAreFeatureFlagsReady()
  const flagValues = useFeatureFlags<boolean>(TRIAL_GATE_FLAG_NAMES)

  // Keyed on the values rather than the array, which useFeatureFlags rebuilds every render:
  // activeTrialGateTypes is part of this hook's public result, and a consumer putting it in a
  // dependency array would otherwise get an effect that re-fires on every render.
  //
  // A type is active when *its* flag is on, looked up by name. The index alignment is between the
  // flag names and their values (useFeatureFlags' own contract), never between a type and a value -
  // two types share one flag, so there is no such thing as a type's index in this result.
  const flagValuesKey = flagValues.map(Boolean).join(',')
  const activeTrialGateTypes = useMemo(() => {
    const values = flagValuesKey.split(',')
    const enabledFlags = new Set(TRIAL_GATE_FLAG_NAMES.filter((_, index) => values[index] === 'true'))
    return TRIAL_GATE_TYPES.filter((type) => enabledFlags.has(TRIAL_GATES[type].flag))
  }, [flagValuesKey])

  const pendingTrialGateTypes = useMemo(() => {
    if (!trialGate.isInScope) return NO_TYPES
    if (!areFeatureFlagsReady) return TRIAL_GATE_TYPES
    return trialGate.isLoading ? activeTrialGateTypes : NO_TYPES
  }, [activeTrialGateTypes, areFeatureFlagsReady, trialGate.isInScope, trialGate.isLoading])

  // Both inputs are already stable references - useTrialGate returns one of three module-level
  // constants, and activeTrialGateTypes is memoized above - so a plain memo keeps this identity
  // stable for consumers that put it in a dependency array.
  const blockedAppointmentTypes = useMemo(
    () => trialGate.blockedAppointmentTypes.filter((type) => activeTrialGateTypes.includes(type)),
    [trialGate.blockedAppointmentTypes, activeTrialGateTypes]
  )

  // Formatting stays inside the memo: it builds an Intl.DateTimeFormat, the only non-trivial work in
  // this hook, and nothing needs it unless something is actually blocked. Keyed on the ISO string
  // because useTrialGate re-parses trialEndDate into a fresh Date instance on every render.
  const trialEndDateIso = trialGate.trialEndDate?.toISOString()
  const reasons = useMemo<TrialGateReasons>(() => {
    if (!blockedAppointmentTypes.length) return NO_REASONS
    const lastTrialDay = formatLastTrialDay(trialEndDateIso)
    return Object.fromEntries(
      blockedAppointmentTypes.map((type) => [type, t(getTrialGate(type)?.reasonKey ?? '', { trialEndDate: lastTrialDay })])
    )
  }, [blockedAppointmentTypes, trialEndDateIso, t])

  // Nothing is decidable until the flags are known, and reporting "settled, nothing blocked" here is
  // what let a fast click through the type-selection screen before the rule could apply.
  if (!areFeatureFlagsReady) {
    return { ...INERT_RESULT, isInScope: trialGate.isInScope, isLoading: trialGate.isInScope, pendingTrialGateTypes }
  }

  // If every trial-gate direction is off (the default everywhere today), stay fully inert rather
  // than leaking useTrialGate's raw isTrialDataUnknown/isLoading/trialEndDate through - otherwise
  // an out-of-scope customer with no trialEndDate would still fire the fallback Mixpanel event
  // even though the feature is supposed to be off.
  if (!activeTrialGateTypes.length) return INERT_RESULT

  return { ...trialGate, blockedAppointmentTypes, reasons, activeTrialGateTypes, pendingTrialGateTypes }
}

export { useBlockedAppointmentType }
export type { TrialGateReasons }
